import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/dialogs/Modal';
import { Select2 } from '@/components/forms/Select2';
import { AsyncButton } from '@/components/common/AsyncButton';
import { onlineManagerApi } from '@/api/services/onlineManager';
import { ContextHeader, type OnlineRowContext } from './RowContext';

interface Props {
  ctx: OnlineRowContext;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddCreditTransactionModal({ ctx, onClose, onSuccess }: Props) {
  const [operationType, setOperationType] = useState('');
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);
  const { data: creditOperationTypes = [] } = useQuery({
    queryKey: ['online-manager', 'credit-operation-types'],
    queryFn: onlineManagerApi.getCreditOperationTypes,
    staleTime: Infinity,
  });

  const opErr = operationType === '' ? 'Operation Type is required.' : '';
  const amtNum = Number(amount);
  const amtErr = amount === '' ? 'Amount is required.' : !(amtNum > 0) ? 'Amount must be a positive number.' : '';
  const valid = !opErr && !amtErr;

  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    await onlineManagerApi.addCreditTransaction({
      login: ctx.login,
      dataSourceId: ctx.dataSourceId,
      operationType: Number(operationType),
      amount: amtNum,
      comment,
    });
    onSuccess();
  };

  return (
    <Modal
      show
      title="Crediti Facility"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-danger btn-sm" onClick={onClose}>Cancel</button>
          <AsyncButton className="btn btn-primary btn-sm" onClick={submit}>Submit Credit</AsyncButton>
        </>
      }
    >
      <ContextHeader ctx={ctx} />
      <div className="mb-3">
        <label className="form-label">Operation Type <span className="text-danger">*</span></label>
        <Select2 placeholder="Select operation" allowClear value={operationType} options={creditOperationTypes} onChange={(v) => setOperationType(v ?? '')} />
        {touched && opErr && <div className="text-danger small mt-1">{opErr}</div>}
      </div>
      <div className="mb-3">
        <label className="form-label">Amount <span className="text-danger">*</span></label>
        <input type="number" min="0" step="any" className={`form-control${touched && amtErr ? ' is-invalid' : ''}`} value={amount} onChange={(e) => setAmount(e.target.value)} />
        {touched && amtErr && <div className="invalid-feedback d-block">{amtErr}</div>}
      </div>
      <div className="mb-0">
        <label className="form-label">Comment</label>
        <textarea className="form-control" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
      </div>
    </Modal>
  );
}
